/**
 * 사내결재 문서함 — [부서별 월간보고서] 탭 (v2.280.0 · 2026-10-19 · 사용자 「월간보고서를 부서별 월간보고서로 통합」)
 *
 * 세 종류를 한곳에 보관한다 — 기술지원팀(기술지원 포털이 보내 옴) · 영업팀(매출 분석 창에서 상신) ·
 * 부서별(경영지원부·기업부설연구소가 여기서 작성). v2.113.0~v2.279.0 의 [기술지원팀 월간보고서]·[영업팀 월간보고서] 두 탭을 합쳤다.
 *
 * ⭐[📝 보고서 작성] 은 **내 소속**으로 갈린다(서버 GET /expense/dept-report/me 가 정한다 — 화면은 그 판정을 그대로 쓴다):
 *   경영지원부·기업부설연구소 → 작성 창 / 시스템사업부 영업 → 매출 분석의 월간보고서 창(기존 방식 · 그 달로) /
 *   기술지원팀 → 「기술지원 포털에서 작성된 자료가 넘어온다」 안내 / 소속 없음 → 안내.
 *
 * 가시성은 서버가 행 단위로 거른다 — 작성자 본인 + 결재선 당사자 + admin.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import * as api from '../../lib/expense-api';
import {
  checkDeptReport, deptPeriodText, getDeptReportMe, periodOptions, type DeptReportRow,
} from '../../lib/dept-report-api';
import { EXPENSE_STATUS_BADGE } from '../../components/ExpenseDocModal';
import { TspReportModal } from '../../components/TspReportModal';
import { SalesMonthlyReportModal } from '../../components/SalesMonthlyReportModal';
import { DeptMonthlyReportModal } from '../../components/DeptMonthlyReportModal';
import { DeptMonthlyReportFormModal } from '../../components/DeptMonthlyReportFormModal';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { LoadingLines } from '../../components/ui/Skeleton';

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

/**
 * 목록 행에서 대상 월을 뽑는다.
 * ⚠목록 API 는 본문을 싣지 않는다(상세에만 있다). 제목이 「… 월간보고 - 2026.09월」 꼴이라(세 종류 모두)
 *   거기서 읽되, 형식이 다르면 **아무것도 지어내지 않고** 빈칸으로 둔다.
 */
function periodFromTitle(title: string | null): string {
  const m = /(\d{4})[.\-/\s]*(\d{1,2})\s*월/.exec(title ?? '');
  return m ? deptPeriodText(`${m[1]}-${String(m[2]).padStart(2, '0')}`) : '';
}

/** 어느 부서의 보고서인가 — 종류·작성자 부서로 (더하기 판정 · 모르는 종류는 종류 이름 그대로) */
function deptLabel(r: api.ExpenseDocListRow): string {
  if (r.docType === '기술지원팀월간보고서') return '기술지원팀';
  if (r.docType === '영업팀월간보고서') return '영업팀';
  if (r.docType === '부서월간보고서') return r.authorDept ?? '부서';
  return r.docType;
}

type OpenDoc = { id: number; type: api.ApprovalDocType };

export function DeptMonthlyTab() {
  const navigate = useNavigate();
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [q, setQ] = useState('');
  const [openDoc, setOpenDoc] = useState<OpenDoc | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState<{ periodYm: string } | null>(null);

  // 내 소속으로 작성 단추가 할 일 + 지난 보고서(가져오기용)
  const me = useQuery({ queryKey: ['dept-report', 'me'], queryFn: getDeptReportMe, staleTime: 60_000 });
  const [periodYm, setPeriodYm] = useState<string | null>(null);
  const chosenYm = periodYm ?? me.data?.suggestedPeriodYm ?? '';
  const months = me.data ? periodOptions(me.data.startYm) : [];

  // 목록은 지출결재와 **같은 통로** — group=monthly 가 월간보고서 세 종류만 남긴다
  const finalQuery = useQuery({
    queryKey: ['expense', 'docs', 'monthly', 'final', year, q],
    queryFn: () => api.listExpenseDocs({ group: 'monthly', final: true, year, q: q.trim() || undefined }),
    staleTime: 15_000,
  });
  const openQuery = useQuery({
    queryKey: ['expense', 'docs', 'monthly', 'open'],
    queryFn: () => api.listExpenseDocs({ group: 'monthly', notFinal: true }),
    staleTime: 15_000,
  });
  const pending = openQuery.data?.rows ?? [];
  const rows = finalQuery.data?.rows ?? [];

  /** [📝 보고서 작성] — 서버가 정한 mode 대로 */
  async function startWrite() {
    const info = me.data;
    if (!info) return;
    setNotice(null);
    if (info.mode === 'tsp') {
      setNotice('기술지원팀 월간보고서는 기술지원 포털(TSP)에서 작성해 보내 오는 자료입니다 — 포털에서는 만들지 않습니다. '
        + '기술지원 포털에서 월간보고서를 확정하면 이 탭에 「진행 중」으로 올라오고, 결재는 내 결재함에서 합니다.');
      return;
    }
    if (info.mode === 'none') {
      setNotice(`소속 부서 정보가 없어 보고서를 만들 수 없습니다 (내 부서: ${info.dept ?? '없음'}). 관리자에게 직원 정보(부서)를 확인해 달라고 해 주세요.`);
      return;
    }
    if (info.mode === 'sales') {
      // 기존 방식 그대로 — 매출 분석의 [📄 월간보고서] 창을 그 달로 연다(숫자는 포털이 집계 · 사람은 특이사항만)
      navigate(`/analysis?report=${chosenYm}`);
      return;
    }
    // form — 그 달이 이미 있으면 그 문서를 연다(작성자는 거기서 [✏ 수정])
    const [y, m] = chosenYm.split('-').map(Number) as [number, number];
    setChecking(true);
    try {
      const c = await checkDeptReport(y, m);
      if (c.existing) {
        setNotice(`${deptPeriodText(c.periodYm)} 보고서는 이미 있습니다 (${c.existing.docNo} · ${c.existing.status}) — 그 문서를 엽니다.`);
        setOpenDoc({ id: c.existing.docId, type: '부서월간보고서' });
        return;
      }
      if (!c.canSubmit) {
        setNotice(c.reason ?? '지금은 보고서를 만들 수 없습니다.');
        return;
      }
      setForm({ periodYm: c.periodYm });
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setChecking(false);
    }
  }

  const modeText = (() => {
    const info = me.data;
    if (!info) return '';
    if (info.mode === 'form') return `${info.deptKey} 보고서를 여기서 씁니다`;
    if (info.mode === 'sales') return '영업팀 보고서 — 매출 분석의 월간보고서 창이 열립니다';
    if (info.mode === 'tsp') return '기술지원팀 — 기술지원 포털에서 작성한 자료가 넘어옵니다';
    return '소속 부서 정보가 없습니다';
  })();

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">부서별 월간보고서</div>
        <ul className="list-disc pl-5 space-y-0.5 text-[13px] text-blue-800">
          <li>
            <b>기술지원팀</b>(기술지원 포털에서 보내 옴) · <b>영업팀</b>(<Link to="/analysis" className="underline">매출 분석</Link>의 [📄 월간보고서] 창) ·
            <b> 경영지원부 · 기업부설연구소</b>(아래 [📝 보고서 작성]) — 월간보고서 세 종류를 한곳에 보관합니다.
          </li>
          <li>결재선은 <b>담당(작성자) → 부서장 → 대표이사</b> 3칸입니다 — 경영지원 승인 단계가 없어 부서장 승인 다음이 곧 최종 승인 차례입니다.</li>
          <li>부서마다 한 달에 <b>한 건</b>만 올립니다. 진행 중인 결재는 <Link to="/approvals" className="underline">내 결재함</Link>에서도 처리할 수 있습니다.</li>
        </ul>
      </div>

      {/* 작성 — 내 소속으로 갈린다 */}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          대상 월
          <select value={chosenYm} onChange={(e) => setPeriodYm(e.target.value)} className="ui-input ui-input-sm"
            disabled={!me.data || months.length === 0}>
            {months.map((ym) => <option key={ym} value={ym}>{deptPeriodText(ym)}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => void startWrite()} disabled={!me.data || checking}
          className="btn btn-primary" title={modeText}>
          {checking ? '확인 중…' : '📝 보고서 작성'}
        </button>
        {me.data && <span className="text-xs text-slate-500">{modeText}</span>}
      </div>
      {notice && (
        <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2 flex items-start gap-2">
          <span className="flex-1">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="text-amber-700 hover:text-amber-900 shrink-0" title="닫기">✕</button>
        </div>
      )}

      {/* ① 결재 진행 중 (작성중·반려 포함 — 작성자가 여기서 다시 들어가 고친다) */}
      {pending.length > 0 && (
        <section>
          <h3 className="ui-subsection-title mb-2">
            진행 중 <span className="text-slate-400 font-normal">{pending.length}건</span>
          </h3>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-[132px]">문서번호</th>
                  <th className="text-left px-3 py-2 font-medium w-[104px]">상태</th>
                  <th className="text-left px-3 py-2 font-medium w-[128px]">부서</th>
                  <th className="text-left px-3 py-2 font-medium w-[104px]">대상 월</th>
                  <th className="text-left px-3 py-2 font-medium w-[92px]">작성자</th>
                  <th className="text-left px-3 py-2 font-medium">제목</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => (
                  <tr key={r.id} onClick={() => setOpenDoc({ id: r.id, type: r.docType })}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${EXPENSE_STATUS_BADGE[r.status] ?? ''}`}>
                        {r.status}
                      </span>
                      {/* 작성중인 기술지원팀 문서 = 연결 시험으로 접수된 것. 결재선을 타지 않는다 */}
                      {r.docType === '기술지원팀월간보고서' && r.status === '작성중' && (
                        <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">시험</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{deptLabel(r)}</td>
                    <td className="px-3 py-2 text-slate-600">{periodFromTitle(r.purpose)}</td>
                    <td className="px-3 py-2">{r.authorName}</td>
                    <td className="px-3 py-2 truncate">{r.purpose ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ② 결재완료 */}
      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h3 className="ui-subsection-title">
            결재완료 <span className="text-slate-400 font-normal">{rows.length}건</span>
          </h3>
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="ui-input">
              {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
            </select>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="문서번호·작성자·제목 검색…" className="ui-input w-60" />
          </div>
        </div>

        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium w-[132px]">문서번호</th>
                <th className="text-left px-3 py-2 font-medium w-[128px]">부서</th>
                <th className="text-left px-3 py-2 font-medium w-[104px]">대상 월</th>
                <th className="text-left px-3 py-2 font-medium w-[110px]">작성일</th>
                <th className="text-left px-3 py-2 font-medium w-[92px]">작성자</th>
                <th className="text-left px-3 py-2 font-medium">제목</th>
                <th className="px-3 py-2 font-medium w-[52px]"></th>
              </tr>
            </thead>
            <tbody>
              {finalQuery.isPending ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400"><LoadingLines /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                  결재완료된 월간보고서가 없습니다.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => setOpenDoc({ id: r.id, type: r.docType })}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                  <td className="px-3 py-2 text-slate-700">{deptLabel(r)}</td>
                  <td className="px-3 py-2 text-slate-600">{periodFromTitle(r.purpose)}</td>
                  <td className="px-3 py-2 text-slate-600">{r.docDate}</td>
                  <td className="px-3 py-2">{r.authorName}</td>
                  <td className="px-3 py-2 truncate">{r.purpose ?? ''}</td>
                  <td className="px-3 py-2 text-center">
                    {r.attachmentId && (
                      <button type="button" title="결재본 보기"
                        onClick={(e) => {
                          e.stopPropagation();
                          openServerPdfPreview({
                            pdfUrl: api.attachmentDownloadUrl(r.attachmentId!),
                            filename: `${r.docNo}_${deptLabel(r)}월간보고서.pdf`,
                          });
                        }}
                        className="text-slate-400 hover:text-blue-600">📄</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 종류마다 다른 창 — 더하기(허용 목록). 모르는 종류는 아무 창도 안 연다(지출결재 창으로 흘러가지 않게) */}
      {openDoc?.type === '기술지원팀월간보고서' && (
        <TspReportModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
      {openDoc?.type === '영업팀월간보고서' && (
        <SalesMonthlyReportModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
      {openDoc?.type === '부서월간보고서' && (
        // 닫을 때 위 안내 띠도 걷는다 — 창 안에서 다시 상신했으면 「임시저장했습니다」가 거짓이 된다
        <DeptMonthlyReportModal docId={openDoc.id} onClose={() => { setOpenDoc(null); setNotice(null); }} />
      )}
      {form && me.data && me.data.deptKey && (
        <DeptMonthlyReportFormModal
          mode="create"
          dept={me.data.deptKey}
          initialPeriodYm={form.periodYm}
          initialPrevRows={[] as DeptReportRow[]}
          initialCurRows={[] as DeptReportRow[]}
          author={{ dept: me.data.dept ?? me.data.deptKey, rank: me.data.rank, name: me.data.name }}
          startYm={me.data.startYm}
          defaultCategories={me.data.defaultCategories}
          latest={me.data.latest}
          onClose={() => setForm(null)}
          onSaved={(submitted, docId) => {
            setForm(null);
            void me.refetch();
            void openQuery.refetch();
            setNotice(submitted ? '결재 상신했습니다 — 부서장 → 대표이사 순으로 결재됩니다.' : '임시저장했습니다 — 아래 「진행 중」에서 다시 열어 고치거나 상신합니다.');
            setOpenDoc({ id: docId, type: '부서월간보고서' });
          }}
        />
      )}
    </div>
  );
}
